/**
 * Console store: health/auth gates, snapshot, logs, toasts, WS dispatch.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from "react";

import { ApiError, api } from "./api";
import { isCodeCollectState, isGateKind, isGateState, paintDigitStrip, phaseStem } from "./digits";
import { uid } from "./format";
import { appendOperatorLine, operatorFrameUpdate } from "./operatorLog";
import type {
  CallSetup,
  ConsoleSnapshot,
  LogLine,
  Toast,
  ToastLevel,
  UiActions,
  WsEvent,
} from "./types";
import { ABNORMAL_CAUSES, EMPTY_SNAPSHOT, sipFromLight } from "./types";
import { connectEvents } from "./ws";

const MAX_TOAST = 5;

export type BootStatus = "booting" | "ready" | "error";

export interface StoreState {
  boot: BootStatus;
  bootError: string | null;
  setup: boolean;
  authenticated: boolean;
  wsOpen: boolean;
  snapshot: ConsoleSnapshot;
  operatorLog: LogLine[];
  debugLog: LogLine[];
  toasts: Toast[];
  newCallOpen: boolean;
  dialPrefill: CallSetup | null;
}

type Action =
  | { type: "boot"; setup: boolean; authenticated: boolean; snapshot?: ConsoleSnapshot }
  | { type: "boot_error"; message: string }
  | { type: "ws"; open: boolean }
  | { type: "event"; event: WsEvent }
  | { type: "toast"; level: ToastLevel; message: string }
  | { type: "dismiss_toast"; id: string }
  | { type: "new_call"; open: boolean; prefill?: CallSetup | null }
  | { type: "tick_park" }
  | { type: "logout" };

const INITIAL: StoreState = {
  boot: "booting",
  bootError: null,
  setup: true,
  authenticated: false,
  wsOpen: false,
  snapshot: EMPTY_SNAPSHOT,
  operatorLog: [],
  debugLog: [],
  toasts: [],
  newCallOpen: false,
  dialPrefill: null,
};

function pushLog(lines: LogLine[], line: string, ts?: string): LogLine[] {
  return appendOperatorLine(lines, line, ts);
}

/** Apply amd_label from this frame, then append the engine line as sent. */
function applyOperatorFrame(
  state: StoreState,
  frame: { type: string; line?: string; amd?: string | null; ts?: string },
): Pick<StoreState, "snapshot" | "operatorLog"> {
  const update = operatorFrameUpdate(state.snapshot.amd_label ?? "", frame);
  const amdLabel = update.amdLabel || state.snapshot.amd_label;
  const snapshot =
    amdLabel && amdLabel !== state.snapshot.amd_label
      ? mergeSnapshot(state.snapshot, {
          amd_label: amdLabel,
          party: { ...state.snapshot.party, amd: amdLabel },
        })
      : state.snapshot;
  return {
    snapshot,
    operatorLog: update.append ? pushLog(state.operatorLog, update.line, frame.ts) : state.operatorLog,
  };
}

function mergeSnapshot(current: ConsoleSnapshot, patch: Partial<ConsoleSnapshot>): ConsoleSnapshot {
  const next: ConsoleSnapshot = {
    ...current,
    ...patch,
    party: { ...current.party, ...(patch.party ?? {}) },
    actions: { ...current.actions, ...(patch.actions ?? {}) },
  };
  // Poll snapshots omit language/script when the session API has not landed
  // those fields. Keep the hello values. Explicit clears assign after merge.
  if (patch.spokenLanguage == null && current.spokenLanguage) next.spokenLanguage = current.spokenLanguage;
  if (patch.scriptId == null && current.scriptId) next.scriptId = current.scriptId;
  if (patch.scriptNode == null && current.scriptNode) next.scriptNode = current.scriptNode;
  return next;
}

function phaseRetry(detail: string | null | undefined): boolean {
  return phaseStem(detail) === "retry";
}

function clearCodeStrip(snapshot: ConsoleSnapshot, digitKind: "gate" | "otp" | "" = ""): ConsoleSnapshot {
  return mergeSnapshot(snapshot, { digits: "", digitKind, codeMatch: null });
}

function applyEvent(state: StoreState, event: WsEvent): StoreState {
  switch (event.type) {
    case "hello": {
      const sipPayload = event.sip && typeof event.sip === "object" ? event.sip : null;
      const sipText = event.text ?? sipPayload?.text;
      const sipCss = event.css ?? sipPayload?.css;
      const ivr = event.ivr && typeof event.ivr === "object" ? event.ivr : null;
      // null/"" clears. || would keep the previous call's label. Absent stays previous.
      const incomingAmd = event.amd_label !== undefined ? event.amd_label : event.amd;
      const amdCleared = incomingAmd !== undefined && !incomingAmd;
      const amdLabel = amdCleared
        ? null
        : event.amd_label || event.amd || state.snapshot.amd_label;
      let snapshot = state.snapshot;
      if (sipText || sipCss) {
        const sip = sipFromLight(sipText, sipCss);
        snapshot = mergeSnapshot(snapshot, {
          sip: event.status ?? sip.status,
          sipText: sip.text,
          sipCss: sip.css,
        });
      }
      snapshot = mergeSnapshot(snapshot, {
        amd_label: amdLabel,
        spokenLanguage: event.spoken_language ?? snapshot.spokenLanguage,
        scriptId: (ivr?.id ? String(ivr.id) : null) || event.pack || snapshot.scriptId,
        scriptNode: ivr?.node ? String(ivr.node) : snapshot.scriptNode,
        party: {
          ...snapshot.party,
          amd: amdCleared ? null : amdLabel || snapshot.party.amd,
        },
      });
      return {
        ...state,
        setup: event.setup ?? state.setup,
        authenticated: event.authenticated ?? state.authenticated,
        snapshot,
      };
    }
    case "snapshot": {
      const incoming = event.console;
      let snapshot = mergeSnapshot(state.snapshot, incoming);
      if (
        incoming.state === "ORIGINATING" ||
        incoming.state === "IDLE" ||
        isGateState(incoming.state, incoming.detail)
      ) {
        snapshot = clearCodeStrip(snapshot, isGateState(incoming.state, incoming.detail) ? "gate" : "");
      } else if (isCodeCollectState(incoming.state, incoming.detail)) {
        snapshot = mergeSnapshot(snapshot, {
          digitKind: "otp",
          digits: incoming.digits || state.snapshot.digits,
        });
      } else if (state.snapshot.digitKind === "otp" && state.snapshot.digits) {
        snapshot = mergeSnapshot(snapshot, {
          digits: state.snapshot.digits,
          digitKind: "otp",
        });
      }
      return { ...state, snapshot };
    }
    case "session.state": {
      // log.operator is the event.log. Do not synthesize a step from state
      // (client "Listening" used to race the engine "Connecting" line).
      const detail = event.detail ?? null;
      let snapshot = mergeSnapshot(state.snapshot, {
        state: event.state,
        detail,
        party: { ...state.snapshot.party, uuid: event.uuid ?? state.snapshot.party.uuid },
      });
      if (event.state === "ORIGINATING" || event.state === "IDLE") {
        snapshot = clearCodeStrip(snapshot, "");
        snapshot.scriptId = null;
        snapshot.scriptNode = null;
        snapshot.spokenLanguage = null;
        snapshot.amd_label = null;
        snapshot.party.amd = null;
      } else if (isGateState(event.state, detail)) {
        snapshot = clearCodeStrip(snapshot, "gate");
      } else if (phaseRetry(detail) && !phaseRetry(state.snapshot.detail)) {
        snapshot = clearCodeStrip(snapshot, "otp");
      } else if (isCodeCollectState(event.state, detail)) {
        snapshot = mergeSnapshot(snapshot, { digitKind: "otp" });
      }
      return { ...state, snapshot };
    }
    case "session.dtmf": {
      // kind=gate never writes the OTP strip. Only mirror_live / otp may.
      if (isGateKind(event.kind)) {
        return { ...state, snapshot: mergeSnapshot(state.snapshot, { digitKind: "gate" }) };
      }
      if (!paintDigitStrip(event.kind)) return state;
      const digits = event.digits ?? `${state.snapshot.digits}${event.digit ?? ""}`;
      return {
        ...state,
        snapshot: mergeSnapshot(state.snapshot, { digits, digitKind: "otp" }),
      };
    }
    case "session.decision": {
      const action = String(event.action);
      const snapshot = action === "Retry" ? clearCodeStrip(state.snapshot, "otp") : state.snapshot;
      return {
        ...state,
        snapshot,
        operatorLog: pushLog(state.operatorLog, action),
      };
    }
    case "session.ended": {
      const framed = applyOperatorFrame(state, {
        type: "session.ended",
        line: event.line,
        amd: event.amd,
      });
      return {
        ...state,
        snapshot: mergeSnapshot(framed.snapshot, {
          state: "DONE",
          party: {
            ...framed.snapshot.party,
            hangup_cause: event.hangup_cause ?? state.snapshot.party.hangup_cause,
          },
        }),
        operatorLog: framed.operatorLog,
      };
    }
    case "ui.hero":
      return {
        ...state,
        snapshot: mergeSnapshot(state.snapshot, {
          heroCopy: event.copy ?? state.snapshot.heroCopy,
          detail: event.detail === undefined ? state.snapshot.detail : event.detail,
          amd_label:
            event.amd_label === undefined
              ? event.amd === undefined
                ? state.snapshot.amd_label
                : event.amd
              : event.amd_label,
          far_end_class:
            event.far_end_class === undefined
              ? state.snapshot.far_end_class
              : event.far_end_class,
          judgment_outcome:
            event.judgment_outcome === undefined
              ? state.snapshot.judgment_outcome
              : event.judgment_outcome,
          trunk_id: event.trunk_id === undefined ? state.snapshot.trunk_id : event.trunk_id,
          otp_state: event.otp_state === undefined ? state.snapshot.otp_state : event.otp_state,
          party: {
            dest: event.dest ?? state.snapshot.party.dest,
            name: event.name ?? state.snapshot.party.name,
            cli: event.cli ?? state.snapshot.party.cli,
            // null clears; ?? treats JSON null as missing and keeps the previous call.
            amd:
              event.amd_label === undefined
                ? event.amd === undefined
                  ? state.snapshot.party.amd
                  : event.amd
                : event.amd_label,
            attempt: event.attempt ?? state.snapshot.party.attempt,
            max_attempts: event.max_attempts ?? state.snapshot.party.max_attempts,
            park_remaining_s:
              event.park_remaining_s === undefined
                ? state.snapshot.party.park_remaining_s
                : event.park_remaining_s,
            last_decision:
              event.last_decision === undefined
                ? state.snapshot.party.last_decision
                : event.last_decision,
            hangup_cause: state.snapshot.party.hangup_cause,
            uuid: state.snapshot.party.uuid,
          },
        }),
      };
    case "ui.code": {
      const collecting =
        isCodeCollectState(state.snapshot.state, state.snapshot.detail) || state.snapshot.digitKind === "otp";
      if (!collecting || state.snapshot.digitKind === "gate") {
        return {
          ...state,
          snapshot: mergeSnapshot(state.snapshot, {
            expected_code: event.expected,
            codeMatch: null,
          }),
        };
      }
      return {
        ...state,
        snapshot: mergeSnapshot(state.snapshot, {
          digits: event.entered,
          expected_code: event.expected,
          codeMatch: event.match ?? null,
          digitKind: "otp",
        }),
      };
    }
    case "ui.actions": {
      const actions: UiActions = {
        accept: Boolean(event.accept),
        retry: Boolean(event.retry),
        resend: Boolean(event.resend),
        hangup: Boolean(event.hangup),
        otp_sent: Boolean(event.otp_sent),
        new_call: event.new_call === undefined ? state.snapshot.actions.new_call : Boolean(event.new_call),
        redial: Boolean(event.redial),
        next_lead: Boolean(event.next_lead),
      };
      return { ...state, snapshot: mergeSnapshot(state.snapshot, { actions }) };
    }
    case "sip.light": {
      const sip = sipFromLight(event.text, event.css);
      return {
        ...state,
        snapshot: mergeSnapshot(state.snapshot, {
          sip: event.status ?? sip.status,
          sipText: sip.text,
          sipCss: sip.css,
        }),
      };
    }
    case "ui.park_tick":
      return {
        ...state,
        snapshot: mergeSnapshot(state.snapshot, {
          party: { ...state.snapshot.party, park_remaining_s: event.remaining_s },
        }),
      };
    case "sys.health":
      return {
        ...state,
        snapshot: mergeSnapshot(state.snapshot, {
          mode: event.mode ?? state.snapshot.mode,
        }),
      };
    case "log.operator":
      return { ...state, ...applyOperatorFrame(state, event) };
    case "log.debug":
      return { ...state, debugLog: pushLog(state.debugLog, event.line, event.ts) };
    default:
      return state;
  }
}

function reducer(state: StoreState, action: Action): StoreState {
  switch (action.type) {
    case "boot":
      return {
        ...state,
        boot: "ready",
        bootError: null,
        setup: action.setup,
        authenticated: action.authenticated,
        snapshot: action.snapshot ? mergeSnapshot(state.snapshot, action.snapshot) : state.snapshot,
      };
    case "boot_error":
      return { ...state, boot: "error", bootError: action.message };
    case "ws":
      return { ...state, wsOpen: action.open };
    case "event":
      return applyEvent(state, action.event);
    case "toast": {
      const toast: Toast = { id: uid("toast"), level: action.level, message: action.message };
      const toasts = [...state.toasts, toast];
      return {
        ...state,
        toasts: toasts.length > MAX_TOAST ? toasts.slice(toasts.length - MAX_TOAST) : toasts,
      };
    }
    case "dismiss_toast":
      return { ...state, toasts: state.toasts.filter((item) => item.id !== action.id) };
    case "new_call":
      return {
        ...state,
        newCallOpen: action.open,
        dialPrefill: action.open ? (action.prefill ?? state.dialPrefill) : null,
      };
    case "tick_park": {
      const remaining = state.snapshot.party.park_remaining_s;
      if (remaining === null || remaining === undefined || remaining <= 0) return state;
      return {
        ...state,
        snapshot: mergeSnapshot(state.snapshot, {
          party: { ...state.snapshot.party, park_remaining_s: remaining - 1 },
        }),
      };
    }
    case "logout":
      return {
        ...state,
        authenticated: false,
        snapshot: EMPTY_SNAPSHOT,
        newCallOpen: false,
        dialPrefill: null,
      };
    default:
      return state;
  }
}

interface StoreApi {
  state: StoreState;
  abnormal: boolean;
  toast: (message: string, level?: ToastLevel) => void;
  dismissToast: (id: string) => void;
  openNewCall: (open?: boolean, prefill?: CallSetup | null) => void;
  run: (fn: () => Promise<unknown>, fallback?: string) => Promise<boolean>;
  operator: {
    accept: () => Promise<boolean>;
    retry: () => Promise<boolean>;
    resend: () => Promise<boolean>;
    hangup: () => Promise<boolean>;
    otpSent: () => Promise<boolean>;
    redial: () => Promise<boolean>;
    nextLead: () => Promise<boolean>;
  };
  logout: () => Promise<void>;
  refreshConsole: () => Promise<void>;
}

const StoreContext = createContext<StoreApi | null>(null);

/**
 * App-wide console store. Keep this above the router outlet so Console can stay mounted.
 */
export function StoreProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const booted = useRef(false);

  const toast = useCallback((message: string, level: ToastLevel = "error") => {
    dispatch({ type: "toast", level, message });
  }, []);

  const run = useCallback(
    async (fn: () => Promise<unknown>, fallback = "Request failed"): Promise<boolean> => {
      try {
        await fn();
        return true;
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.status === 401) dispatch({ type: "logout" });
          const suffix = err.status === 409 ? " (busy)" : err.status === 403 ? " (CSRF)" : "";
          toast(`${err.detail || fallback}${suffix}`, "error");
        } else if (err instanceof Error) {
          toast(err.message || fallback, "error");
        } else {
          toast(fallback, "error");
        }
        return false;
      }
    },
    [toast],
  );

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    let cancelled = false;
    (async () => {
      try {
        const health = await api.health();
        const setup = health.setup !== false;
        let authenticated = false;
        if (setup) {
          try {
            const me = await api.me();
            authenticated = Boolean(me.authenticated);
          } catch {
            authenticated = false;
          }
        }
        let snapshot: ConsoleSnapshot | undefined;
        if (setup && authenticated) {
          try {
            snapshot = await api.session();
          } catch {
            snapshot = undefined;
          }
        }
        if (!cancelled) dispatch({ type: "boot", setup, authenticated, snapshot });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Cannot reach CDIALER";
        if (!cancelled) dispatch({ type: "boot_error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (state.boot !== "ready" || !state.setup || !state.authenticated) return;
    const dispose = connectEvents(
      (event) => dispatch({ type: "event", event }),
      (open) => {
        dispatch({ type: "ws", open });
        // hello/ui.hero/ui.actions carry no session.state. A call started or
        // ended during the gap would stay stale (2 s poll is off in IDLE/DONE).
        if (!open) return;
        void api
          .session()
          .then((snapshot) => dispatch({ type: "event", event: { type: "snapshot", console: snapshot } }))
          .catch(() => undefined);
      },
    );
    return dispose;
  }, [state.boot, state.setup, state.authenticated]);

  useEffect(() => {
    if (state.boot !== "ready" || !state.authenticated) return;
    const phase = state.snapshot.state;
    if (phase === "IDLE" || phase === "DONE") return;
    let cancelled = false;
    const id = window.setInterval(() => {
      void api
        .session()
        .then((snapshot) => {
          if (!cancelled) dispatch({ type: "event", event: { type: "snapshot", console: snapshot } });
        })
        .catch(() => undefined);
    }, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [state.authenticated, state.boot, state.snapshot.state]);

  useEffect(() => {
    if (state.snapshot.state !== "PARKED") return;
    if (state.snapshot.party.park_remaining_s === null) return;
    const id = window.setInterval(() => dispatch({ type: "tick_park" }), 1000);
    return () => window.clearInterval(id);
  }, [state.snapshot.state, state.snapshot.party.park_remaining_s]);

  const apiSurface = useMemo<StoreApi>(() => {
    const cause = (state.snapshot.party.hangup_cause ?? "").toUpperCase();
    const abnormal = ABNORMAL_CAUSES.has(cause);
    return {
      state,
      abnormal,
      toast,
      dismissToast: (id: string) => dispatch({ type: "dismiss_toast", id }),
      openNewCall: (open = true, prefill = null) =>
        dispatch({ type: "new_call", open, prefill }),
      run,
      operator: {
        accept: () => run(() => api.decide("Accept"), "Accept failed"),
        retry: () => run(() => api.decide("Retry"), "Retry failed"),
        resend: () => run(() => api.decide("Resend"), "Resend failed"),
        hangup: () => run(() => api.hangup(), "Hangup failed"),
        otpSent: () => run(() => api.otpSent(), "OTP sent failed"),
        redial: () => run(() => api.redial(), "Redial failed"),
        nextLead: async () => {
          try {
            const data = await api.leads();
            const lead = (data.leads ?? []).find(
              (item) => item.id !== null && (item.status === "pending" || item.status === "queued"),
            ) ?? (data.leads ?? []).find((item) => item.id !== null);
            if (!lead?.id) {
              toast("No leads loaded", "warn");
              return false;
            }
            const setup = await api.leadDialForm(lead.id);
            dispatch({ type: "new_call", open: true, prefill: setup });
            return true;
          } catch (err) {
            const detail = err instanceof ApiError ? err.detail : "Next lead failed";
            toast(detail, "error");
            return false;
          }
        },
      },
      logout: async () => {
        await run(() => api.logout(), "Logout failed");
        dispatch({ type: "logout" });
      },
      refreshConsole: async () => {
        try {
          const snapshot = await api.session();
          dispatch({ type: "event", event: { type: "snapshot", console: snapshot } });
        } catch {
          /* live WS will catch up */
        }
      },
    };
  }, [run, state, toast]);

  return <StoreContext.Provider value={apiSurface}>{children}</StoreContext.Provider>;
}

/**
 * Access the console store. Must be under {@link StoreProvider}.
 */
export function useStore(): StoreApi {
  const value = useContext(StoreContext);
  if (!value) throw new Error("useStore must be used within StoreProvider");
  return value;
}
